import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import LoadingButton from "_shared/_views/loading/loading-button";
import TagInput from "_shared/_views/tags/tag-input";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleTemplatesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import { CanceledError } from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import FeeScheduleTemplateCommandAPIService from "work/fee-schedule-templates/_api/commands/fee-schedule-template-command-api-service";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";

const PageContainer = styled(Grid)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const DetailsContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(5),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
  width: "100%",
  "& > .MuiTextField-root": {
    width: "50%",
  },
}));
const SaveButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

type CreateTemplateFeeScheduleTemplateDialogProps = {
  feeSchedule?: FeeScheduleTemplate;
  templateId?: Guid;
  name?: string;
  categories?: FeeScheduleCategory[];
  entityId?: Guid;
  viewOnly?: boolean;
  onSave?: (feeSchedule: FeeScheduleTemplate) => void;
  onDirty?: (dirty: boolean) => void;
};

export default function CreateFeeScheduleTemplateDialog(props: Readonly<CreateTemplateFeeScheduleTemplateDialogProps>) {
  const { feeSchedule, templateId, viewOnly, onSave, onDirty } = props;

  const nameFieldRef = React.useRef<HTMLInputElement>(null);

  const [name, setName] = React.useState<string>(props.name ?? "");
  const [tags, setTags] = React.useState<FeeScheduleTag[]>([]);
  const [categories, setCategories] = React.useState<
    Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>
  >(props.categories ?? []);
  const [originalTemplate, setOriginalTemplate] = React.useState<FeeScheduleTemplate | undefined>(undefined);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [formDirty, setFormDirty] = React.useState<boolean>(false);

  const session = useSession();

  const loadFromFeeScheduleTemplateId = React.useCallback(
    async (
      templateId: Guid,
      abortController: AbortController
    ): Promise<FeeScheduleTemplate | undefined> => {
      try {
        const feeScheduleService = new FeeScheduleTemplateQueryAPIService(session);
        return FeeScheduleTemplate.fromJSON(
          await feeScheduleService.getFeeScheduleTemplateById(templateId, abortController)
        );
      } catch (error) {
        if (error instanceof CanceledError) return;
        console.error(error);
        enqueueSnackbar("Couldn't fetch fee schedule from template. Please try again", { variant: "error" });
        return;
      }
    },
    [session]
  );

  const loadExistingFeeSchedule = React.useCallback(
    async (abortController: AbortController) => {
      if (!feeSchedule && !templateId) return;

      // templateId was provided, try to load
      if (templateId) {
        const templateFeeSchedule = await loadFromFeeScheduleTemplateId(templateId, abortController);

        if (!templateFeeSchedule) {
          setName(name ?? "");
          setTags([]);
          setCategories(categories ?? []);
          return;
        }

        setOriginalTemplate(templateFeeSchedule);
        setName(templateFeeSchedule?.name);
        setTags(templateFeeSchedule?.tags);
        setCategories(templateFeeSchedule?.categories);
        return;
      }

      // The fee schedule was already provided via prop
      setOriginalTemplate(feeSchedule);
      setName(feeSchedule?.name ?? "");
      setTags(feeSchedule?.tags ?? []);
      setCategories(feeSchedule?.categories ?? []);
    },
    [
      feeSchedule,
      templateId,
      loadFromFeeScheduleTemplateId,
      name,
      categories
    ]
  );

  useEffect(() => {
    let abortController = new AbortController();
    void loadExistingFeeSchedule(abortController);
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [loadExistingFeeSchedule]);

  function isFormValid(): boolean {
    return !_.isEmpty(name) && !_.isEmpty(categories);
  }

  function getIsCategoryAlreadySelected(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): boolean {
    return categories.some((c) => c.isEqualTo(category));
  }

  async function handleSaveClicked() {
    setSaving(true);

    try {
      if (!session.user?.id) throw new Error("User id not found");
      if (!session.user.legalEntityId) throw new Error("Entity id not found");

      let newTemplate = new FeeScheduleTemplate({
        name,
        tags,
        categories,
        creator: new EntityMember({
          userId: session.user.id,
          legalEntityId: session.user.legalEntityId,
        }),
      });
      const feeScheduleService = new FeeScheduleTemplateCommandAPIService(session);
      newTemplate = FeeScheduleTemplate.fromJSON(
        await feeScheduleService.createFeeScheduleTemplate(newTemplate, session.accountType)
      );
      enqueueSnackbar("Created fee schedule", { variant: "success" });
      onSave?.(newTemplate);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't save fee schedule. Please try again", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveChangesClicked() {
    if (!originalTemplate?.id) {
      console.warn("Attempted to save invalid update: missing originalTemplate.id");
      return;
    }
    setSaving(true);

    let updatedTemplate = new FeeScheduleTemplate({
      name,
      id: originalTemplate.id,
      tags,
      categories,
    });

    try {
      const feeScheduleService = new FeeScheduleTemplateCommandAPIService(session);
      updatedTemplate = FeeScheduleTemplate.fromJSON(
        await feeScheduleService.updateFeeScheduleTemplate(originalTemplate, updatedTemplate)
      );
      enqueueSnackbar("Updated fee schedule", { variant: "success" });

      onSave?.(updatedTemplate);
      onDirty?.(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't save fee schedule changes. Please try again", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Handles when a category is added
   * @param category The category to add
   */
  async function handleCategoryAdded(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (getIsCategoryAlreadySelected(category)) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }
    onDirty?.(true);
    setCategories((prevCategories) => [...prevCategories, category]);
    setFormDirty(true);
  }

  /**
   * Handles when a category is removed
   * @param name The name of the category to remove
   */
  async function handleCategoryRemoved(name: FeeScheduleCategoryName) {
    onDirty?.(true);
    const updatedCategories = categories.filter((category) => category.name?.isEqualTo(name) === false);
    setCategories(updatedCategories);
    setFormDirty(true);
  }

  /**
   * Handles when a category is updated
   * @param originalName The original name of the category
   * @param updatedCategory The updated fee category
   */
  async function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  ) {
    if (getIsCategoryAlreadySelected(updatedCategory)) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }

    onDirty?.(true);

    const updatedCategories = [...categories].filter((category) => category.name?.isEqualTo(originalName!) === false);
    updatedCategories.push(updatedCategory);

    // HACK: There is a bug in MaterialTable that requires the list
    // to be cleared before setting the new value
    setCategories([]);
    setCategories(updatedCategories);
    setFormDirty(true);
  }

  function handleExistingTemplateSelected(template: FeeScheduleTemplate, replace: boolean) {
    onDirty?.(true);

    if (replace) {
      setCategories(template.categories);
    } else {
      const duplicateCategories = template.categories.filter(
        (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => getIsCategoryAlreadySelected(category)
      );
      setCategories((prevCategories) => [
        ...prevCategories,
        ...template.categories.filter(
          (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => !duplicateCategories.includes(category)
        ),
      ]);
      if (duplicateCategories.length > 0) {
        enqueueSnackbar(
          "Some categories were not added because categories with the same name already exist in the fee schedule",
          { variant: "warning" }
        );
      }
    }

    setFormDirty(true);
    // The focus call will fail if there isn't a slight delay
    setTimeout(() => nameFieldRef.current?.focus(), 100);
  }

  return (
    <React.Fragment>
      <PageContainer container direction="row">
        {!viewOnly && (
          <DetailsContainer>
            <TextField
              inputRef={nameFieldRef}
              required
              label="Name"
              disabled={viewOnly}
              helperText="The template's name"
              slotProps={{
                htmlInput: { maxLength: 50 },
              }}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setFormDirty(true);
                onDirty?.(true);
              }}
            />
            <TagInput
              helperText={`Tags to help you find this template later (ex. US, ${new AHDate().year})`}
              viewOnly={viewOnly}
              onTagsUpdated={(tagValues) => {
                setTags(tagValues.map((tagValue) => new FeeScheduleTag(tagValue)));
                setFormDirty(true);
                onDirty?.(true);
              }}
              onTagRemoved={(tagValue) => {
                setTags((prevTags) => prevTags.filter((prevTag) => prevTag.value !== tagValue));
                setFormDirty(true);
                onDirty?.(true);
              }}
              tags={tags.map((tag) => tag.value)}
            />
          </DetailsContainer>
        )}
        <Grid>
          <FeeScheduleTemplatesTable
            categories={categories}
            disableEditing={viewOnly}
            commentsToolTip={""}
            hideCommentButton={true}
            onCategoryAdded={handleCategoryAdded}
            onCategoryRemoved={handleCategoryRemoved}
            onCategoryUpdated={handleCategoryUpdated}
            onExistingTemplateSelected={handleExistingTemplateSelected}
          />
        </Grid>
      </PageContainer>
      {!viewOnly && (
        <SaveButton
          color="primary"
          startIcon={<SaveIcon />}
          loading={saving}
          variant="contained"
          disabled={!formDirty || !isFormValid() || saving}
          onClick={originalTemplate ? handleSaveChangesClicked : handleSaveClicked}
        >
          {originalTemplate ? "Save Changes" : "Save"}
        </SaveButton>
      )}
    </React.Fragment>
  );
}
